import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Service for managing authentication-related API calls
 */
export const authService = {
  /**
   * Login to the application
   * @param {string} identifier Email or username
   * @param {string} password User's password
   * @returns {Promise<Object>} Login response with token and user data
   */
  login: async (identifier, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      identifier,
      password
    });
    return response.data;
  },

  /**
   * Sign up a new user
   * @param {string} name User's full name
   * @param {string} email User's email
   * @param {string} password User's password
   * @param {string} role User's role (default: 'user')
   * @param {Object} additionalData Additional data for signup (e.g., claimToken, autoVerify)
   * @returns {Promise<Object>} Signup response
   */
  signup: async (name, email, password, role = 'user', additionalData = {}) => {
    const requestData = {
      name,
      email,
      password,
      role,
      ...additionalData
    };
    
    // Remove image_url if it's null to avoid overriding with null
    if (additionalData.image_url === null) {
      delete requestData.image_url;
    }
    
    const response = await axios.post(`${API_URL}/api/auth/signup`, requestData);
    return response.data;
  },

  /**
   * Verify email with verification code
   * @param {string} email User's email
   * @param {string} code Verification code
   * @returns {Promise<Object>} Verification response with token and user data
   */
  verifyEmail: async (email, code) => {
    const response = await axios.post(`${API_URL}/api/auth/verify-email`, {
      email,
      code
    });
    return response.data;
  },

  /**
   * Resend verification code
   * @param {string} email User's email
   * @returns {Promise<Object>} Response data
   */
  resendVerification: async (email) => {
    const response = await axios.post(`${API_URL}/api/auth/resend-verification`, {
      email
    });
    return response.data;
  },

  /**
   * Request a password reset
   * @param {string} email User's email
   * @returns {Promise<Object>} Response with status message
   */
  forgotPassword: async (email) => {
    const response = await axios.post(`${API_URL}/api/auth/forgot-password`, {
      email
    });
    return response.data;
  },

  /**
   * Reset password with token
   * @param {string} token Reset token from email
   * @param {string} password New password
   * @returns {Promise<Object>} Response with status message
   */
  resetPassword: async (token, password) => {
    const response = await axios.post(`${API_URL}/api/auth/reset-password`, {
      token,
      password
    });
    return response.data;
  },

  /**
   * Verify email with token from link
   * @param {string} token Verification token
   * @returns {Promise<Object>} Verification response with token and user data
   */
  verifyToken: async (token) => {
    const response = await axios.get(`${API_URL}/api/auth/verify-token?token=${token}`);
    return response.data;
  },

  /**
   * Get current user profile
   * @returns {Promise<Object>} User profile data
   */
  getProfile: async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    // Add a local cache check to avoid unnecessary API calls
    const cachedUserString = localStorage.getItem('user');
    const cachedTimestamp = localStorage.getItem('userCacheTimestamp');
    const now = Date.now();
    
    // If we have a recent cache (less than 1 minute old), use it
    if (cachedUserString && cachedTimestamp && (now - parseInt(cachedTimestamp)) < 60000) {
      clearTimeout(timeoutId);
      console.log('Using cached user data');
      return JSON.parse(cachedUserString);
    }
    
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        signal: controller.signal,
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      clearTimeout(timeoutId);
      
      // Update cache timestamp
      if (response.data) {
        localStorage.setItem('userCacheTimestamp', now.toString());
      }
      
      return response.data;
    } catch (error) {
      clearTimeout(timeoutId);
      
      // If it's an abort error, provide a clearer message
      if (error.name === 'AbortError' || error.code === 'ECONNABORTED') {
        console.error('Request timed out. Server may be overloaded.');
        
        // If we have cached data, return it even if expired as fallback
        if (cachedUserString) {
          console.log('Falling back to cached user data after timeout');
          return JSON.parse(cachedUserString);
        }
        
        throw new Error('Request timed out. Please try again later.');
      }
      
      // For other errors, pass them along
      throw error;
    }
  }
};

export default authService; 